import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "messageList", "input",
    "statusDot", "statusText", "statusDot2", "statusText2",
    "unreadBadge", "channelItem", "channelPreview"
  ]
  static values = {
    wsUrl:         String,
    token:         String,
    userId:        String,
    channelUrl:    String,
    myAvatar:      String,
    otherAvatar:   String,
    myNickname:    String,
    otherNickname: String,
    isBroadcast:   Boolean,
    appId:         String
  }

  connect() {
    this._openWs()
    this._scrollBottom()
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("keydown", this._onKeydown.bind(this))
      this.inputTarget.addEventListener("input",   this._autoResize.bind(this))
    }
    this._restoreUnread()
    this._clearUnread(this.channelUrlValue)
  }

  disconnect() {
    // _destroyed 플래그로 이후 _openWs 재진입 차단
    this._destroyed = true
    // 예약된 재연결 타이머가 있으면 취소 (언마운트 후 좀비 WS 방지)
    clearTimeout(this._reconnectTimer)
    if (this._ws) {
      // onclose 핸들러를 먼저 제거 — close() 호출 시 onclose 가 발화되어
      // 재연결 setTimeout 이 등록되는 것을 막기 위함
      this._ws.onclose = null
      this._ws.close()
    }
  }

  send() {
    if (!this.hasInputTarget) return
    const text = this.inputTarget.value.trim()
    if (!text) return
    const now = Date.now()
    this._appendMessage({
      type:        "MESG",
      channel_url: this.channelUrlValue,
      user_id:     this.userIdValue,
      message:     text,
      created_at:  now
    })
    this.inputTarget.value = ""
    this._autoResize()
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return
    this._ws.send(JSON.stringify({
      type:        "MESG",
      channel_url: this.channelUrlValue,
      message:     text,
      custom_type: "text",
      created_at:  now
    }))
    this._sentTs || (this._sentTs = new Set())
    this._sentTs.add(now)
  }

  _onKeydown(e) {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) { e.preventDefault(); this.send() }
  }

  _autoResize() {
    if (!this.hasInputTarget) return
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = Math.min(el.scrollHeight, 96) + "px"
  }

  _openWs() {
    // 컨트롤러가 이미 언마운트됐으면 재연결 안함 (좀비 WS 방지)
    if (this._destroyed) return
    const appId = this.appIdValue || "app-001"
    const url = `${this.wsUrlValue}/v3/ws?token=${encodeURIComponent(this.tokenValue)}&user_id=${encodeURIComponent(this.userIdValue)}&app_id=${encodeURIComponent(appId)}`
    this._ws  = new WebSocket(url)

    this._ws.onopen = () => {
      this._setStatus("LIVE", "#39FF14")
      this.channelItemTargets.forEach(el => {
        const ch = el.dataset.channel
        if (ch) this._ws.send(JSON.stringify({ type: "ENTER", channel_url: ch, user_id: this.userIdValue, created_at: Date.now() }))
      })
    }

    this._ws.onmessage = (event) => {
      for (const line of event.data.split('\n')) {
        const s = line.trim()
        if (!s) continue
        let msg
        try { msg = JSON.parse(s) } catch { continue }

        if (msg.type === "MEMBER_INVITED") {
          this._handleMemberInvited(msg)
          continue
        }

        if (msg.type !== "MESG") continue
        if (msg.user_id === this.userIdValue && this._sentTs?.has(msg.created_at)) {
          this._sentTs.delete(msg.created_at)
          continue
        }
        if (msg.channel_url === this.channelUrlValue) {
          this._appendMessage(msg)
        } else {
          this._updatePreview(msg.channel_url, msg.message)
          this._incrementUnread(msg.channel_url, msg)
        }
      }
    }

    this._ws.onclose = () => {
      this._setStatus("OFFLINE", "#555577")
      // disconnect() 에서 clearTimeout 으로 재연결을 취소할 수 있도록 타이머 참조 저장
      this._reconnectTimer = setTimeout(() => this._openWs(), 3000)
    }

    this._ws.onerror = () => this._setStatus("ERROR", "#FF2E63")
  }

  _appendMessage(msg) {
    if (!this.hasMessageListTarget) return

    const isSystem = msg.user_id === "system-webxcom"
    const goNick  = msg.user?.nickname || ""
    const isMine  = !isSystem && (
      msg.user_id === this.userIdValue ||
      (goNick && goNick === this.myNicknameValue && goNick !== this.otherNicknameValue)
    )
    const ts   = msg.created_at > 9_999_999_999 ? Math.floor(msg.created_at / 1000) : msg.created_at
    const time = new Date(ts * 1000).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })

    const row = document.createElement("div")

    if (isSystem) {
      row.className = "cn-mrow sys"
      row.innerHTML = `
        <div>
          <div class="cn-mbubble sys">${this._escape(msg.message)}</div>
          <div class="cn-mtime" style="text-align:center;padding:0;margin-top:3px;">${time}</div>
        </div>`
    } else {
      const avatar = isMine ? this.myAvatarValue : this.otherAvatarValue
      const nick   = goNick || (isMine ? this.myNicknameValue : this.otherNicknameValue)
      const avEl   = avatar
        ? `<img src="${avatar}" class="cn-mav" alt="" />`
        : `<div class="cn-mph">${this._escape((nick || "?")[0].toUpperCase())}</div>`

      row.className = `cn-mrow${isMine ? " me" : ""}`
      row.innerHTML = `
        ${avEl}
        <div class="cn-mbody">
          <div class="cn-mnick">${this._escape(nick || "")}</div>
          <div class="cn-mbubble ${isMine ? "me" : "other"}">${this._escape(msg.message)}</div>
          <div class="cn-mtime">${time}</div>
        </div>`
    }

    this.messageListTarget.appendChild(row)
    this._scrollBottom()
    this._updatePreview(this.channelUrlValue, msg.message)
  }

  _incrementUnread(channelUrl, msg) {
    // WS 재연결 시 Go-Chat이 과거 메시지를 재전송할 수 있음 → created_at 기반 중복 방지
    // cx_lts_: 이 채널에서 마지막으로 카운팅한 메시지의 created_at (밀리초)
    const tsKey  = `cx_lts_${channelUrl}`
    const lastTs = parseInt(localStorage.getItem(tsKey) || "0")
    const msgTs  = msg?.created_at || 0

    // 이미 처리한 타임스탬프 이하면 재전송된 메시지 — 카운트 스킵
    if (msgTs > 0 && msgTs <= lastTs) return

    // 새 메시지: 마지막 처리 타임스탬프 갱신
    if (msgTs > 0) localStorage.setItem(tsKey, msgTs)

    const countKey = `cx_unread_${channelUrl}`
    const next     = (parseInt(localStorage.getItem(countKey) || "0")) + 1
    localStorage.setItem(countKey, next)
    this._updateBadge(channelUrl, next)
  }

  _clearUnread(channelUrl) {
    // 안읽음 카운트만 초기화 — cx_lts_ (마지막 처리 타임스탬프)는 유지
    // cx_lts_ 를 지우면 재연결 시 과거 메시지가 다시 카운팅되므로 절대 삭제 금지
    localStorage.removeItem(`cx_unread_${channelUrl}`)
    this._updateBadge(channelUrl, 0)
  }

  _restoreUnread() {
    this.unreadBadgeTargets.forEach(badge => {
      const ch      = badge.dataset.channel
      const lsCount = parseInt(localStorage.getItem(`cx_unread_${ch}`) || "0")
      const lsLts   = parseInt(localStorage.getItem(`cx_lts_${ch}`)    || "0")

      if (lsCount > 0) {
        // localStorage에 이미 카운트가 있으면 그대로 사용
        this._updateBadge(ch, lsCount)
      } else if (lsLts === 0) {
        // localStorage에 아무 데이터 없음 → 서버 렌더 시 계산한 초기값으로 초기화
        // 이 경우는 채팅 페이지 밖에 있다가 진입했을 때 해당
        const serverUnread = parseInt(badge.dataset.serverUnread || "0")
        const serverLts    = parseInt(badge.dataset.serverLts    || "0")
        if (serverUnread > 0 && serverLts > 0) {
          // 카운트와 타임스탬프 모두 초기화 — 이후 WS 재전송이 와도 중복 카운팅 방지
          localStorage.setItem(`cx_unread_${ch}`, serverUnread)
          localStorage.setItem(`cx_lts_${ch}`,    serverLts)
          this._updateBadge(ch, serverUnread)
        }
      }
    })
  }

  _updateBadge(channelUrl, count) {
    const badge = this.unreadBadgeTargets.find(b => b.dataset.channel === channelUrl)
    if (!badge) return
    if (count > 0) {
      badge.textContent = count > 99 ? "99+" : String(count)
      badge.classList.add("on")
    } else {
      badge.textContent = ""
      badge.classList.remove("on")
    }
  }

  _updatePreview(channelUrl, text) {
    const preview = this.channelPreviewTargets.find(p => p.dataset.channel === channelUrl)
    if (!preview) return
    const short = String(text).slice(0, 28)
    preview.textContent = short.length < String(text).length ? short + '…' : short
  }

  _scrollBottom() {
    if (!this.hasMessageListTarget) return
    const el = this.messageListTarget
    el.scrollTop = el.scrollHeight
  }

  _setStatus(label, color) {
    for (const el of this.statusDotTargets)   el.style.background = color
    for (const el of this.statusTextTargets)  { el.textContent = label; el.style.color = color }
    for (const el of this.statusDot2Targets)  el.style.background = color
    for (const el of this.statusText2Targets) { el.textContent = label; el.style.color = color }
  }

  _handleMemberInvited(msg) {
    if (window.Turbo) {
      Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }

  _escape(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
